"use client";

import { c as _c } from "react/compiler-runtime";
import { propertyTypeOptions } from "@/info/unit/property-types";
import { Filters } from "@/types/filters";
import { useEffect, useMemo, useRef } from "react";
import { Label } from "react-aria-components";
import type { Control, UseFormSetValue } from "react-hook-form";
import { Controller, useWatch } from "react-hook-form";
import { RadioGroupInput } from "../rac/radio-group-input";
import type { PropertyType } from "../search/filters/desktop";
import { PropertyTypeIconGrid } from "./property-type-icon-grid";
import { SwitchField } from "./switch-field";

// Define the structure of property type options for better type safety
interface PropertyTypeOption {
  value: string;
  label: string;
  finalValue?: Record<string, unknown>;
  options?: PropertyTypeOption[];
  isCompleted?: boolean;
}

// More flexible interface that works with different form structures
interface PropertyTypeFieldProps {
  control: Control<Filters>;
  name?: keyof Filters | string; // Name of the property type field in the form
  exactPropertyTypeName?: keyof Filters | string; // Name of the exact property type field
  isCommunityFieldName?: keyof Filters | string; // Name of the isCommunity field
  setValue: UseFormSetValue<Filters>;
  label?: string;
  required?: boolean;
  excludeOnly?: boolean; // If true, excludes the "Only" option from the property type selectio
  exactTypeRequired?: boolean; // If true, the exact property type is required
  hideLabel?: boolean;
  options?: typeof propertyTypeOptions;
  markFieldDirty?: (fieldName: keyof Filters) => void; // Hack to manually mark fields as dirty
}
export function PropertyTypeField(t0) {
  const $ = _c(46);
  const {
    control,
    name: t1,
    exactPropertyTypeName,
    isCommunityFieldName: t2,
    excludeOnly,
    setValue,
    label: t3,
    required,
    exactTypeRequired,
    hideLabel: t4,
    options: t5,
    markFieldDirty
  } = t0;
  const name = t1 === undefined ? "propertyType" : t1;
  const isCommunityFieldName = t2 === undefined ? "isCommunity" : t2;
  const label = t3 === undefined ? "Property type" : t3;
  const hideLabel = t4 === undefined ? false : t4;
  const options = t5 === undefined ? propertyTypeOptions : t5;
  const t6 = name as keyof Filters;
  let t7;
  if ($[0] !== control || $[1] !== t6) {
    t7 = {
      control,
      name: t6
    };
    $[0] = control;
    $[1] = t6;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const typeValue = useWatch(t7) as unknown as PropertyType;
  const t8 = exactPropertyTypeName as keyof Filters;
  let t9;
  if ($[3] !== control || $[4] !== t8) {
    t9 = {
      control,
      name: t8
    };
    $[3] = control;
    $[4] = t8;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const exactTypeValue = useWatch(t9) as string;
  const previousTypeValue = useRef(typeValue);
  let t10;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = propertyTypeOptions.reduce(_temp, {} as Record<string, PropertyTypeOption[]>);
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const exactPropertyTypeOptions = t10;
  let t11;
  let t12;
  if ($[7] !== exactPropertyTypeName || $[8] !== setValue || $[9] !== typeValue) {
    t11 = () => {
      if (previousTypeValue.current !== typeValue && exactPropertyTypeName) {
        if (typeValue) {
          const anyOption = exactPropertyTypeOptions[typeValue]?.find(_temp2);
          if (anyOption) {
            setValue(exactPropertyTypeName as keyof Filters, anyOption.value, {
              shouldDirty: true
            });
          }
        } else {
          setValue(exactPropertyTypeName as keyof Filters, "", {
            shouldDirty: true
          });
        }
      }
      previousTypeValue.current = typeValue;
    };
    t12 = [typeValue, setValue, exactPropertyTypeName, exactPropertyTypeOptions];
    $[7] = exactPropertyTypeName;
    $[8] = setValue;
    $[9] = typeValue;
    $[10] = t11;
    $[11] = t12;
  } else {
    t11 = $[10];
    t12 = $[11];
  }
  useEffect(t11, t12);
  let t13;
  if ($[12] !== excludeOnly || $[13] !== typeValue) {
    t13 = excludeOnly ? (exactPropertyTypeOptions[typeValue] || []).filter(_temp3) : (exactPropertyTypeOptions[typeValue] || []).filter(_temp4);
    $[12] = excludeOnly;
    $[13] = typeValue;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const subTypeOptions = t13;
  let t14;
  if ($[15] !== hideLabel || $[16] !== label) {
    t14 = !hideLabel && <Label className="text-title-medium">{label}</Label>;
    $[15] = hideLabel;
    $[16] = label;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  const t15 = name as keyof Filters;
  const t16 = required ? "Please select a property type" : false;
  let t17;
  if ($[18] !== t16) {
    t17 = {
      required: t16
    };
    $[18] = t16;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] !== markFieldDirty || $[21] !== name || $[22] !== options || $[23] !== required || $[24] !== setValue) {
    t18 = t19 => {
      const {
        field: t20,
        fieldState
      } = t19;
      const {
        value: value_0
      } = t20;
      return <div className="flex flex-col gap-2"><PropertyTypeIconGrid options={options} required={required} errorMessage={fieldState.error?.message} value={value_0 as PropertyType} onChange={newValue => {
          setValue(name as keyof Filters, newValue as PropertyType, {
            shouldDirty: true,
            shouldTouch: true
          });
          if (markFieldDirty) {
            markFieldDirty(name as keyof Filters);
          }
        }} /></div>;
    };
    $[20] = markFieldDirty;
    $[21] = name;
    $[22] = options;
    $[23] = required;
    $[24] = setValue;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== control || $[27] !== t15 || $[28] !== t17 || $[29] !== t18) {
    t19 = <Controller control={control} name={t15} rules={t17} render={t18} />;
    $[26] = control;
    $[27] = t15;
    $[28] = t17;
    $[29] = t18;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== control || $[32] !== exactPropertyTypeName || $[33] !== exactTypeRequired || $[34] !== subTypeOptions || $[35] !== typeValue) {
    t20 = exactPropertyTypeName && typeValue && subTypeOptions.length > 0 && <Controller control={control} name={exactPropertyTypeName as keyof Filters} rules={{
      required: exactTypeRequired ? "Please select exact property type" : false
    }} render={t21 => {
      const {
        field: t22,
        fieldState: fieldState_0
      } = t21;
      const {
        value: value_1,
        onChange,
        onBlur
      } = t22;
      return <RadioGroupInput className="ml-2" label="Exact property type" labelClassName="text-title" options={subTypeOptions} value={value_1 as PropertyType} onChange={onChange} itemsClassName="grid grid-cols-1 md:grid-cols-3" onBlur={onBlur} errorMessage={fieldState_0.error?.message} />;
    }} />;
    $[31] = control;
    $[32] = exactPropertyTypeName;
    $[33] = exactTypeRequired;
    $[34] = subTypeOptions;
    $[35] = typeValue;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== control || $[38] !== exactTypeValue || $[39] !== isCommunityFieldName) {
    t21 = exactTypeValue === "h:p-" && isCommunityFieldName && <SwitchField control={control} name={isCommunityFieldName as keyof Filters} label="Is Community?" className="ml-2" />;
    $[37] = control;
    $[38] = exactTypeValue;
    $[39] = isCommunityFieldName;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t14 || $[42] !== t19 || $[43] !== t20 || $[44] !== t21) {
    t22 = <div className="flex flex-col py-4 my-4 gap-2">{t14}{t19}{t20}{t21}</div>;
    $[41] = t14;
    $[42] = t19;
    $[43] = t20;
    $[44] = t21;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  return t22;
}
function _temp4(opt_1) {
  return !opt_1.label.toLowerCase().includes("any");
}
function _temp3(opt_0) {
  return !["a", "h", "v", "p"].includes(opt_0.value);
}
function _temp2(opt) {
  return opt.label.toLowerCase().includes("any");
}
function _temp(acc, t0) {
  const {
    value,
    options: options_0
  } = t0;
  acc[value] = options_0;
  return acc;
}